/**
 * 权限转换工具函数
 *
 * 在扁平化权限代码（数据库格式）和 Arco Pro 格式之间转换
 */

import type { PermissionCode, UserPermission, PermissionConfig } from '@/types/permission';
import { PERMISSION_MAPPING } from '@/config/permission-mapping';
import { createLogger } from '@/utils/logger';

const logger = createLogger('PermissionTransformer');

/**
 * 将扁平化权限代码转换为 Arco Pro 格式
 *
 * @param permissions - 扁平化权限代码数组（从后端获取）
 * @returns Arco Pro 格式的权限对象
 *
 * @example
 * // 输入: ['business_project:view_all', 'business_project:create', 'presale:view_own']
 * // 输出: { business_project: ['read', 'write'], presale: ['read'] }
 */
export function transformPermissions(permissions: PermissionConfig[]): UserPermission {
  if (!Array.isArray(permissions)) {
    logger.error('权限不是数组', { permissions });
    return {};
  }

  const result: UserPermission = {};

  for (const perm of permissions) {
    if (!perm.is_enabled) continue;

    const mapping = PERMISSION_MAPPING[perm.permission_code];
    if (!mapping) {
      logger.warn(`未找到权限代码映射: ${perm.permission_code}`);
      continue;
    }

    const { resource, action } = mapping;
    // 不标准化 action，保持原始名称（create, change, delete 等）
    // 这样可以与 PermissionWrapper 的检查逻辑匹配
    // const normalizedAction = normalizeAction(action);

    if (!result[resource]) {
      result[resource] = [];
    }

    if (!result[resource].includes(action)) {
      result[resource].push(action);
    }
  }

  return result;
}

/**
 * 将 Arco Pro 格式转换为扁平化权限代码列表
 *
 * @param userPermission - Arco Pro 格式的权限对象
 * @returns 扁平化权限代码数组
 *
 * @example
 * // 输入: { business_project: ['read', 'write'], presale: ['read'] }
 * // 输出: ['business_project:view_all', 'business_project:create', 'presale:view_own']
 */
export function flattenPermissions(userPermission: UserPermission): PermissionCode[] {
  const result: PermissionCode[] = [];

  for (const [resource, actions] of Object.entries(userPermission)) {
    for (const action of actions) {
      // 查找对应的权限代码
      const code = findPermissionCode(resource, action);
      if (code) {
        result.push(code);
      }
    }
  }

  return result;
}

/**
 * 查找权限代码
 *
 * @param resource - 资源名称
 * @param action - 操作类型
 * @returns 权限代码
 */
function findPermissionCode(
  resource: string,
  action: string
): PermissionCode | null {
  // 这里简化处理，实际可能需要更复杂的逻辑
  // 例如根据 resource 和 action 查找最匹配的权限代码

  // 先尝试精确匹配
  const exactMatch = Object.entries(PERMISSION_MAPPING).find(
    ([, { resource: r, action: a }]) => r === resource && a === action
  );

  if (exactMatch) {
    return exactMatch[0] as PermissionCode;
  }

  // 尝试模糊匹配（例如 read 可以匹配 read_all, read_department, read_own）
  if (action === 'read') {
    const readMatch = Object.entries(PERMISSION_MAPPING).find(
      ([, { resource: r, action: a }]) =>
        r === resource && a.startsWith('read')
    );
    if (readMatch) {
      return readMatch[0] as PermissionCode;
    }
  }

  return null;
}

/**
 * 合并多个权限对象
 *
 * @param permissions - 多个权限对象
 * @returns 合并后的权限对象
 */
export function mergePermissions(...permissions: UserPermission[]): UserPermission {
  const result: UserPermission = {};

  for (const perm of permissions) {
    for (const [resource, actions] of Object.entries(perm)) {
      if (!result[resource]) {
        result[resource] = [];
      }

      // 合并并去重
      for (const action of actions) {
        if (!result[resource].includes(action)) {
          result[resource].push(action);
        }
      }
    }
  }

  return result;
}

/**
 * 过滤权限（只保留指定资源的权限）
 *
 * @param userPermission - 用户权限对象
 * @param resources - 要保留的资源列表
 * @returns 过滤后的权限对象
 */
export function filterPermissions(
  userPermission: UserPermission,
  resources: string[]
): UserPermission {
  const result: UserPermission = {};

  for (const resource of resources) {
    if (userPermission[resource]) {
      result[resource] = userPermission[resource];
    }
  }

  return result;
}
